// **React Imports
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

// **MUI Imports
import { MenuOutlined } from '@mui/icons-material'
import {
  AppBar,
  Box,
  Toolbar as MuiToolbar,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material'

// **Context Imports
import { SettingsContext } from '../../context/setting'

// **Custom Components Imports
import UserDropdown from './user-dropdown'

// **Utils Imports
import {
  checkCandidateAccess,
  checkDeveloperAccess,
  hexToRGBA,
  showErrorToast
} from '../../utils/utils'

// **API Imports
import { validateToken } from '../../axios/api/auth'
import { getCandidateProfile } from '../../axios/api/candidate'
import { tokenActive } from '../../axios/api/general'
import { getProfileDetails } from '../../axios/api/profile'
import { getListOfWorkspace } from '../../axios/api/workspace'

// **Image Imports
import LogoLight from '../../assets/images/logo-light.svg'
import {
  default as LogoDark,
  default as LogoWithWordmarkDark
} from '../../assets/images/logo-with-wordmark-dark.svg'

// **Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import { setProfileDetails } from '../../store/slice/auth'
import {
  setSelectedWorkspace,
  setWorkspaces
} from '../../store/slice/workspace'

// **Third party imports

// **SCSS Imports
import './index.scss'

// **Styled Component
const Toolbar = styled(MuiToolbar)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('md')]: {
    padding: '8px 16px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '4px 8px'
  }
}))

// **Seperate Component
const ImageContainer = memo(
  ({ mode, isMobileScreen, isGlobalCandidate = false }) => {
    // hooks
    const { contestUri } = useParams()
    const {
      settings: { sidePanel, tenantConfig }
    } = useContext(SettingsContext)

    // variables
    const showHeaderLogo = useMemo(() => {
      return (
        sidePanel?.hidden || contestUri || isGlobalCandidate || isMobileScreen
      )
    }, [sidePanel?.hidden, contestUri, isMobileScreen, isGlobalCandidate])

    const tenantLogo = useMemo(() => {
      return tenantConfig?.logo
    }, [tenantConfig?.logo])

    const tenantLogoWithWordmark = useMemo(() => {
      return tenantConfig?.wordmark
    }, [tenantConfig?.wordmark])

    return (
      showHeaderLogo && (
        <Box
          className="image-container"
          component={Link}
          to={
            contestUri
              ? `/c/${contestUri}/stages`
              : isGlobalCandidate
              ? '/c'
              : '/'
          }
        >
          {isMobileScreen && (
            <img
              className="image-container-logo"
              src={
                !tenantLogo
                  ? contestUri || isGlobalCandidate
                    ? LogoDark
                    : LogoLight
                  : tenantLogo
              }
              alt="logo"
            />
          )}
          {!isMobileScreen && (
            <img
              className="image-container-wordmark"
              src={tenantLogoWithWordmark || LogoWithWordmarkDark}
              alt="wordmark"
            />
          )}
        </Box>
      )
    )
  }
)

const AppBarContent = memo(() => {
  return (
    <Box className="appbar-content-container">
      <UserDropdown />
    </Box>
  )
})

const Header = () => {
  //Hooks
  const theme = useTheme()
  const { contestUri } = useParams()
  const { settings, setSettings } = useContext(SettingsContext)
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('lg'))
  const dispatch = useDispatch()
  const location = useLocation()

  const workspaces = useSelector((state) => state.workspace.workspaces)
  const selectedWorkspace = useSelector(
    (state) => state.workspace.selectedWorkspace
  )

  // states
  const [loading, setLoading] = useState({
    workspaceLoading: true,
    profileLoading: false
  })

  //Variables
  const { skin, appBar, appBarBlur, appBarHidden, navHidden, mode, sidePanel } =
    settings

  const globalCandidatePath = useMemo(() => {
    return (
      location.pathname.endsWith('/c') ||
      location.pathname.endsWith('/c/') ||
      location.pathname.includes('/c/')
    )
  }, [location.pathname])

  const isCandidate = useMemo(() => checkCandidateAccess(), [])

  const isDeveloper = useMemo(() => checkDeveloperAccess(), [])

  //handlers
  const handleSidePanelToggling = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      sidePanel: {
        ...prev.sidePanel,
        opened: !prev.sidePanel.opened,
        expanded: !prev.sidePanel.opened
      }
    }))
  }, [setSettings])

  const tokenValidation = useCallback(async () => {
    if (contestUri) {
      await validateToken(contestUri, globalCandidatePath) // invited candidate
    } else if (globalCandidatePath) {
      await validateToken(null, globalCandidatePath) // global candidate
    } else {
      await validateToken() // user
    }
  }, [contestUri, globalCandidatePath])

  const setTokenHeartbeat = useCallback(() => {
    tokenActive()
  }, [])

  const fetchAllWorkspace = useCallback(async () => {
    setLoading((prev) => ({ ...prev, workspaceLoading: true }))
    const response = await getListOfWorkspace()
    if (response?.isError) {
      showErrorToast(response)
      dispatch(setWorkspaces({ workspaces: [] }))
    } else {
      dispatch(setWorkspaces({ workspaces: response }))
    }
    setLoading((prev) => ({ ...prev, workspaceLoading: false }))
  }, [dispatch])

  const fetchProfile = useCallback(
    async (isCandidate) => {
      setLoading((prev) => ({ ...prev, profileLoading: true }))
      const response = await (isCandidate
        ? getCandidateProfile()
        : getProfileDetails())
      setLoading((prev) => ({ ...prev, profileLoading: false }))

      if (response?.isError) {
        showErrorToast(response)
        dispatch(setProfileDetails({}))
      } else {
        dispatch(setProfileDetails(response))
      }
    },
    [dispatch]
  )

  //UseEffect
  useEffect(() => {
    if (!isCandidate && !isDeveloper) {
      fetchAllWorkspace()
    }
    if (!isDeveloper) {
      fetchProfile(isCandidate)
    }
  }, [fetchAllWorkspace, fetchProfile, isCandidate, isDeveloper])

  useEffect(() => {
    if (!loading.workspaceLoading) {
      if (workspaces?.length > 0 && selectedWorkspace) {
        const workspace = workspaces.find(
          (item) => item?.id === selectedWorkspace?.id
        )

        if (!workspace) {
          dispatch(setSelectedWorkspace({ selectedWorkspace: workspaces[0] }))
        } else {
          dispatch(setSelectedWorkspace({ selectedWorkspace: workspace }))
        }
      } else if (workspaces?.length > 0 && !selectedWorkspace) {
        dispatch(setSelectedWorkspace({ selectedWorkspace: workspaces[0] }))
      } else {
        dispatch(setSelectedWorkspace({ selectedWorkspace: null }))
      }
    }
  }, [loading.workspaceLoading, workspaces, selectedWorkspace, dispatch])

  //useEffects
  useEffect(() => {
    if (!isDeveloper) {
      tokenValidation()
    }

    return () => {}
  }, [tokenValidation, isDeveloper])

  useEffect(() => {
    let interval
    if (!isDeveloper) {
      setTokenHeartbeat()

      // Call Token Heartbeat every 60sec
      interval = setInterval(() => {
        setTokenHeartbeat()
      }, 60000)
    }
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [setTokenHeartbeat, isDeveloper])

  return (
    !appBarHidden && (
      <AppBar
        color="default"
        className={`header ${appBar === 'fixed' ? 'sticky' : 'static'}`}
        elevation={skin === 'bordered' ? 0 : 2}
        sx={{
          ...(appBar === 'static' && { zIndex: 13 }),
          transition: 'border-bottom 0.2s ease-in-out',
          ...(appBarBlur && { backdropFilter: 'blur(6px)' }),
          backgroundColor: (theme) =>
            hexToRGBA(
              contestUri || isCandidate
                ? theme.palette.primary.main
                : theme.palette.common.white,
              appBarBlur ? 0.95 : 1
            ),
          ...(skin === 'bordered' && {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`
          }),
          boxShadow: 'none'
        }}
      >
        <Box
          className="header-navbar"
          id="navbar"
          sx={{
            width: '100%',
            ...(navHidden
              ? {}
              : {
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                })
          }}
        >
          <Toolbar
            className={`header-content-container full-width`}
            sx={{
              minHeight: (theme) =>
                `${theme.mixins.toolbar.minHeight - 1}px !important`
            }}
          >
            <Box className="header-flex-container">
              {!sidePanel?.hidden &&
                isMobileScreen &&
                !contestUri &&
                !globalCandidatePath && (
                  <MenuOutlined
                    className="menu-toggle-icon"
                    onClick={handleSidePanelToggling}
                  />
                )}
              <ImageContainer
                mode={mode}
                isMobileScreen={isMobileScreen}
                isGlobalCandidate={globalCandidatePath}
              />
            </Box>
            <AppBarContent />
          </Toolbar>
        </Box>
      </AppBar>
    )
  )
}

export default memo(Header)
